import { Component, input, inject, computed, signal } from "@angular/core";
import { NgTemplateOutlet } from "@angular/common";
import { TranslateModule } from "@ngx-translate/core";
import { JumpstartComponentsModule } from "@wk/components-v3-angular17";
import { WkTranslatePipe } from "@utils";
import { FilterService } from "@filter/filter.service";
import { SimpleFilterMulti, DynamicFilterMulti } from "@filter/filters";
import { FilterSelectionKind, FilterViewTypeEnum } from "@filter/filter.types";
import type { SupportedFilterValue, FilterValue } from "@filter/filter.types";

@Component({
    selector: "wk-filter-pills",
    templateUrl: "./filter-pills.component.html",
    styleUrl: "./filter-pills.component.scss",
    host: { class: "wk-fp" },
    imports: [NgTemplateOutlet, TranslateModule, JumpstartComponentsModule, WkTranslatePipe],
})
export class FilterPillsComponent<TItem> {
    protected readonly filterService = inject(FilterService);
    protected readonly FilterSelectionKind = FilterSelectionKind;
    protected readonly FilterViewTypeEnum = FilterViewTypeEnum;

    public filterName = input.required<string>();
    public dynamicFilterName = input<string>();
    public linesCount = input<number>(3);

    protected morePillsCount = signal<number>(0);
    protected headingId = signal<string>("");
    protected isExpanded = signal<boolean>(false);

    protected filter = computed(() => {
        const filterName = this.filterName();
        const filter = this.filterService.getFilter(filterName);
        if (filter instanceof SimpleFilterMulti) return filter;

        const dynamicFilterName = this.dynamicFilterName();
        if (!dynamicFilterName) return;
        return (filter as DynamicFilterMulti<TItem>).getFilter(dynamicFilterName);
    });

    protected filterDefinition = computed(() => this.filter()?.definition);

    protected filterSelectionOptions = computed(() => {
        const filter = this.filter();
        if (!filter) return [];

        const selection = filter.selection();
        if (!selection) return [];

        const definition = this.filterDefinition();
        if (!definition) return [];

        if (!("values" in selection) || !selection.values || !selection.values.length) return [];
        const castedFilter = filter as SimpleFilterMulti<TItem>;
        const options = castedFilter.options?.() || [];
        return selection.values
            .map((value) => options.find((option) => option.value === value))
            .filter((option): option is NonNullable<typeof option> => option !== undefined);
    });

    protected onClose(value: SupportedFilterValue) {
        const field = this.filter();
        if (field instanceof SimpleFilterMulti) field.removeValue(value as FilterValue);
        else field?.clear();
    }

    protected onVisiblePillsChange(event: { morePillsCount: number }): void {
        this.morePillsCount.set(event.morePillsCount);
    }

    protected onExpandButtonToggle(): void {
        this.isExpanded.set(!this.isExpanded());
    }
}
